import { useCallback, useMemo, useState } from 'react';
import {
  ReactFlow,
  Controls,
  ControlButton,
  useReactFlow,
  MiniMap,
  Background,
  BackgroundVariant,
  Panel,
} from '@xyflow/react';
import { toast } from 'react-toastify';
import { CursorArrowRaysIcon, CubeTransparentIcon } from '@heroicons/react/24/outline';

import { useEdges, useNodes, pathwayActions, usePathwayId, useSuggestedEdges, useParentPathwayId } from '@/stores/pathwayStore';
import { useIsPaletteOpen, usePaletteHeight } from '@/stores/settingsStore';
import { useActiveCategory } from '@/stores/registryStore';

import PathwayPanel from './PathwayPanel';
import { proOptions, nodeTypes, edgeTypes, fitViewOptions, defaultViewport } from './pathwayOptions';
import { pathwaysCacheActions, useCachedPanePosition } from '@/stores/pathwaysCacheStore';
import featureConfig from '@/featureConfig';
import useClipboard from '@/hooks/useClipboard';
import NodeContextMenu from '@/components/nodes/NodeContextMenu';

const PathwayFlow = () => {
  const pathwayId = usePathwayId();
  const parentPathwayId = useParentPathwayId();
  const isInternals = !!parentPathwayId;
  const isPaletteOpen = useIsPaletteOpen();
  const activeCategory = useActiveCategory();
  const paletteHeight = usePaletteHeight(activeCategory);
  const labelTopOffset = isPaletteOpen ? paletteHeight : 40;
  const nodes = useNodes();
  const edges = useEdges();
  const suggestedEdges = useSuggestedEdges();
  const reactFlow = useReactFlow();
  const viewportPosition = useCachedPanePosition(pathwayId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialNodesLength = useMemo(() => nodes.length, [pathwayId]);

  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const { onSelectionChange, onPaneClick: onClipboardPaneClick } = useClipboard({ isSelectionMode, setIsSelectionMode, reactFlow });

  const closeContextMenu = useCallback(() => setContextMenu(null), []);

  const onPaneClick = useCallback((e) => {
    closeContextMenu();
    onClipboardPaneClick(e);
  }, [closeContextMenu, onClipboardPaneClick]);

  const onDragOver = useCallback(event => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    event => {
      event.preventDefault();

      if (!featureConfig.manageModules) {
        toast.error('This feature is not available in the academic version');
        return;
      }

      const nodeType = event.dataTransfer.getData('nodeType');

      if (!nodeType) {
        return;
      }

      const position = reactFlow.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      pathwayActions.addNode(nodeType, position);
    },
    [reactFlow],
  );

  const onMoveEnd = (event, data) => {
    pathwaysCacheActions.cachePanePosition(pathwayId, data);
  };

  const getViewportProps = () => {
    if (viewportPosition) return { defaultViewport: viewportPosition };
    if (initialNodesLength > 0) return { fitViewOptions, fitView: true };
    return { defaultViewport };
  };

  const props = getViewportProps();

  const onNodeContextMenu = useCallback((event, node) => {
    event.preventDefault();
    if (!['pathwayNode', 'systemNode', 'balancerNode'].includes(node.type)) return;
    setContextMenu({ x: event.clientX, y: event.clientY, nodeId: node.id });
  }, []);

  const onEdgeClick = (event, edge) => {
    if (edge.data?.suggested) {
      pathwayActions.connectInputToOutput(edge);
    }
  };

  return (
    <div key={pathwayId} className={`relative flex-grow w-full h-full pl-2 pt-2 ${isInternals ? 'bg-slate-200' : 'bg-canvas'}`}>
      <ReactFlow
        nodes={nodes}
        edges={[...edges, ...suggestedEdges]}
        deleteKeyCode={''}
        onNodesChange={pathwayActions.onNodesChange}
        onEdgesChange={pathwayActions.onEdgesChange}
        onNodeDragStop={pathwayActions.onNodeDragStop}
        onConnect={pathwayActions.connectInputToOutput}
        onEdgeClick={onEdgeClick}
        onNodeClick={closeContextMenu}
        onNodeContextMenu={onNodeContextMenu}
        onDrop={onDrop}
        onMoveEnd={onMoveEnd}
        onDragOver={onDragOver}
        proOptions={proOptions}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        selectionOnDrag={isSelectionMode}
        panOnDrag={isSelectionMode ? [1, 2] : true}
        selectionMode={isSelectionMode ? 'partial' : undefined}
        onSelectionChange={onSelectionChange}
        onPaneClick={onPaneClick}
        {...props}
        minZoom={0.2}
      >
        <Background
          color={isInternals ? 'rgba(147,197,253,0.6)' : 'rgba(229,231,235,0.5)'}
          variant={BackgroundVariant.Lines}
          gap={isInternals ? 80 : 40}
        />

        {isInternals && (
          <Panel position="top-left">
            <div style={{ marginTop: labelTopOffset }} className="flex items-center gap-2 bg-blue-100 border border-blue-300 text-blue-700 font-medium text-sm px-3 py-1.5 rounded-md shadow-sm transition-[margin-top] duration-200">
              <CubeTransparentIcon className="h-4 w-4 shrink-0" />
              <span>Editing Module Internals</span>
            </div>
          </Panel>
        )}

        <Panel position="bottom-left" className="flex items-end gap-0">
          <Controls showInteractive={false}>
            <ControlButton
              onClick={() => setIsSelectionMode(prev => !prev)}
              title={isSelectionMode ? 'Exit selection mode' : 'Enter selection mode'}
              className={isSelectionMode ? '!bg-blue-100' : ''}
            >
              <CursorArrowRaysIcon className={`w-4 h-4 ${isSelectionMode ? 'text-blue-600' : ''}`} />
            </ControlButton>
          </Controls>
          {isSelectionMode && (
            <span className="ml-11 mb-1 h-10 text-gray-500 text-xs italic whitespace-nowrap">
              Copy and Paste Mode: Click/drag to select module(s), Ctrl+C/Ctrl+V (⌘C/⌘V on Mac) to copy and paste.
              <br />
              Ctrl+Delete (⌘Delete on Mac) to delete selected module(s). Click button to exit.
            </span>
          )}
        </Panel>
        <MiniMap zoomable pannable />
        <PathwayPanel />
      </ReactFlow>
      {contextMenu && (
        <NodeContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onMirror={() => pathwayActions.mirrorNode(contextMenu.nodeId)}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  );
};

export default PathwayFlow;
